import { hexToRgb, rgbToHex, rgbToHsl, hslToRgb } from './colors';

// Helper to clamp values
const clamp = (val: number, min: number, max: number) => Math.min(Math.max(val, min), max);

// Generate Tints (adding white)
export const generateTints = (hex: string, steps: number = 10): string[] => {
  const rgb = hexToRgb(hex);
  if (!rgb) return [];

  const tints: string[] = [];
  for (let i = 0; i < steps; i++) {
    const factor = (i / steps) * 0.85;
    const r = Math.round(rgb.r + (255 - rgb.r) * factor);
    const g = Math.round(rgb.g + (255 - rgb.g) * factor);
    const b = Math.round(rgb.b + (255 - rgb.b) * factor);
    tints.push(rgbToHex(r, g, b));
  }
  return tints.reverse(); // Darkest to lightest
};

// Generate Shades (adding black)
export const generateShades = (hex: string, steps: number = 10): string[] => {
  const rgb = hexToRgb(hex);
  if (!rgb) return [];

  const shades: string[] = [];
  for (let i = 0; i < steps; i++) {
    const factor = (i / steps) * 0.85;
    const r = Math.round(rgb.r * (1 - factor));
    const g = Math.round(rgb.g * (1 - factor));
    const b = Math.round(rgb.b * (1 - factor));
    shades.push(rgbToHex(r, g, b));
  }
  return shades; // Lightest to darkest
};

// Calculate Luminance
const getLuminance = (r: number, g: number, b: number) => {
  const a = [r, g, b].map((v) => {
    v /= 255;
    return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
};

// Calculate Contrast Ratio
export const getContrastRatio = (hex1: string, hex2: string): number => {
  const rgb1 = hexToRgb(hex1);
  const rgb2 = hexToRgb(hex2);
  if (!rgb1 || !rgb2) return 1;

  const lum1 = getLuminance(rgb1.r, rgb1.g, rgb1.b);
  const lum2 = getLuminance(rgb2.r, rgb2.g, rgb2.b);

  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);

  return (brightest + 0.05) / (darkest + 0.05);
};

export const getWCAGLevel = (ratio: number, fontSize: 'normal' | 'large' = 'normal') => {
  if (fontSize === 'normal') {
    if (ratio >= 7) return 'AAA';
    if (ratio >= 4.5) return 'AA';
    return 'Fail';
  } else {
    if (ratio >= 4.5) return 'AAA';
    if (ratio >= 3) return 'AA';
    return 'Fail';
  }
};

// --- APCA (Accessible Perceptual Contrast Algorithm) ---
// APCA 0.0.98G reference math; supplementary to WCAG 2.x conformance.
// Reference: https://github.com/Myndex/apca-w3 (src/apca-w3.js).

const sRGBtoY = (r: number, g: number, b: number): number => {
  // APCA uses a simple 2.4 power, not the IEC piecewise sRGB transfer function.
  const toLinear = (c: number): number => Math.pow(c / 255, 2.4);
  // Y (luminance) with APCA coefficients
  return 0.2126729 * toLinear(r) + 0.7151522 * toLinear(g) + 0.0721750 * toLinear(b);
};

// Soft clamp for APCA low-luminance adjustment
const softClamp = (Y: number): number => {
  if (Y < 0) return 0;
  if (Y < 0.022) return Y + Math.pow(0.022 - Y, 1.414);
  return Y;
};

/**
 * Calculate APCA contrast value (Lc) between text and background colors.
 * Returns a signed value: positive = dark text on light bg, negative = light text on dark bg.
 * Use Math.abs() for comparison against thresholds.
 */
export const getAPCAContrast = (textHex: string, bgHex: string): number => {
  const textRgb = hexToRgb(textHex);
  const bgRgb = hexToRgb(bgHex);
  if (!textRgb || !bgRgb) return 0;

  const Ytxt = softClamp(sRGBtoY(textRgb.r, textRgb.g, textRgb.b));
  const Ybg = softClamp(sRGBtoY(bgRgb.r, bgRgb.g, bgRgb.b));

  // SAPC power curve constants
  const normBG = 0.56;
  const normTXT = 0.57;
  const revBG = 0.65;
  const revTXT = 0.62;
  const scaleBoW = 1.14;
  const scaleWoB = 1.14;
  const loBoWoffset = 0.027;
  const loWoBoffset = 0.027;
  const loClip = 0.1;

  // Black soft clamp is already applied exactly once above.
  if (Math.abs(Ybg - Ytxt) < 0.0005) return 0;
  let SAPC: number;

  if (Ybg > Ytxt) {
    // Dark text on light background (normal polarity)
    SAPC = (Math.pow(Ybg, normBG) - Math.pow(Ytxt, normTXT)) * scaleBoW;
    return SAPC < loClip ? 0 : (SAPC - loBoWoffset) * 100;
  } else {
    // Light text on dark background (reverse polarity)
    SAPC = (Math.pow(Ybg, revBG) - Math.pow(Ytxt, revTXT)) * scaleWoB;
    return SAPC > -loClip ? 0 : (SAPC + loWoBoffset) * 100;
  }
};

export type APCAUseCase = 'body' | 'heading' | 'ui' | 'decorative';

/**
 * Determine APCA level based on Lc value and use case.
 * Returns 'Pass' or 'Fail' with the minimum Lc threshold.
 */
export const getAPCALevel = (lc: number, useCase: APCAUseCase = 'body'): { pass: boolean; minLc: number; label: string } => {
  const absLc = Math.abs(lc);
  const thresholds: Record<APCAUseCase, { minLc: number; label: string }> = {
    body:       { minLc: 90,  label: 'Body Text (≥90 Lc)' },
    heading:    { minLc: 75,  label: 'Headings (≥75 Lc)' },
    ui:         { minLc: 60,  label: 'UI Components (≥60 Lc)' },
    decorative: { minLc: 30,  label: 'Decorative (≥30 Lc)' },
  };

  const t = thresholds[useCase];
  return { pass: absLc >= t.minLc, minLc: t.minLc, label: t.label };
};

// Generate Palettes
export const generatePalette = (hex: string, type: 'monochromatic' | 'analogous' | 'triadic' | 'split-complementary' | 'tetradic'): string[] => {
  const rgb = hexToRgb(hex);
  if (!rgb) return [];
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b); // Note: utils/colors.ts returns s, l as 0-100

  // Normalize HSL to 0-1 for calculation if needed, but utils/colors returns 0-360, 0-100, 0-100
  // Let's work with the values as returned: h: 0-360, s: 0-100, l: 0-100

  const colors: string[] = [];

  switch (type) {
    case 'monochromatic':
      for (let i = 0; i < 5; i++) {
        // Vary lightness
        const newL = clamp(hsl.l + (i - 2) * 15, 10, 90);
        const newRgb = hslToRgb(hsl.h, hsl.s, newL);
        colors.push(rgbToHex(newRgb.r, newRgb.g, newRgb.b));
      }
      break;

    case 'analogous':
      for (let i = 0; i < 5; i++) {
        const newH = (hsl.h + (i - 2) * 30 + 360) % 360;
        const newRgb = hslToRgb(newH, hsl.s, hsl.l);
        colors.push(rgbToHex(newRgb.r, newRgb.g, newRgb.b));
      }
      break;

    case 'triadic': {
      // 3 core triadic colors
      const triadicHues = [0, 120, 240].map(offset => (hsl.h + offset) % 360);
      for (const th of triadicHues) {
        const newRgb = hslToRgb(th, hsl.s, hsl.l);
        colors.push(rgbToHex(newRgb.r, newRgb.g, newRgb.b));
      }
      // Pad to 5: lighter variant of 2nd, darker variant of 3rd
      const triadLight = hslToRgb(triadicHues[1], hsl.s, clamp(hsl.l + 15, 10, 90));
      colors.push(rgbToHex(triadLight.r, triadLight.g, triadLight.b));
      const triadDark = hslToRgb(triadicHues[2], hsl.s, clamp(hsl.l - 15, 10, 90));
      colors.push(rgbToHex(triadDark.r, triadDark.g, triadDark.b));
      break;
    }

    case 'split-complementary': {
      colors.push(hex);
      const scH1 = (hsl.h + 150) % 360;
      const scH2 = (hsl.h + 210) % 360;
      const scRgb1 = hslToRgb(scH1, hsl.s, hsl.l);
      const scRgb2 = hslToRgb(scH2, hsl.s, hsl.l);
      colors.push(rgbToHex(scRgb1.r, scRgb1.g, scRgb1.b));
      colors.push(rgbToHex(scRgb2.r, scRgb2.g, scRgb2.b));
      // Pad to 5: lighter variant of 2nd, darker variant of 3rd
      const scLight = hslToRgb(scH1, hsl.s, clamp(hsl.l + 15, 10, 90));
      colors.push(rgbToHex(scLight.r, scLight.g, scLight.b));
      const scDark = hslToRgb(scH2, hsl.s, clamp(hsl.l - 15, 10, 90));
      colors.push(rgbToHex(scDark.r, scDark.g, scDark.b));
      break;
    }

    case 'tetradic': {
      // 4 core tetradic colors
      const tetHues = [0, 90, 180, 270].map(offset => (hsl.h + offset) % 360);
      for (const th of tetHues) {
        const newRgb = hslToRgb(th, hsl.s, hsl.l);
        colors.push(rgbToHex(newRgb.r, newRgb.g, newRgb.b));
      }
      // Pad to 5: lightness variant of 2nd color
      const tetVariant = hslToRgb(tetHues[1], hsl.s, clamp(hsl.l + 15, 10, 90));
      colors.push(rgbToHex(tetVariant.r, tetVariant.g, tetVariant.b));
      break;
    }
  }

  return colors;
};
